import { isSelfHosted } from "@/helpers/self-hosted.helper"
import {
  DEFAULT_DOWNLOAD_URL,
  REGCRED_DOCKER_JSON_FILENAME,
  REGCRED_YAML_FILENAME,
  REGISTRY_CREDENTIALS_DOWNLOAD_URL
} from "@/pages/DeploymentsPage/const"
import { PACKAGE_SPEC_ARCH, PACKAGE_SPEC_TYPE } from "../const"

import type { OSPlatform } from "@/icons"
import type { FileWithQueryString } from "@/pages/DeploymentsPage/components/organisms/DownloadStep"

export type FileType = "deployment" | "secret"

export const getKortexFilename = (version: string) => {
  let fileName = "kortex"
  if (version !== "") {
    fileName += `-${version}`
  }
  return fileName + ".tgz"
}

export const getCommonFiles = (version: string) => {
  const files = []

  if (version !== "") {
    files.push({
      name: getKortexFilename(version),
      url: DEFAULT_DOWNLOAD_URL,
      fileType: "deployment"
    })
  }

  if (!isSelfHosted()) {
    files.push({
      name: REGCRED_YAML_FILENAME,
      url: REGISTRY_CREDENTIALS_DOWNLOAD_URL,
      fileType: "secret"
    })
  }

  return files
}

// Docker config.json with the Kodem registry credentials (docker login / az acr import)
export const getDockerRegcredFiles = () => [
  {
    name: REGCRED_DOCKER_JSON_FILENAME,
    url: `${REGISTRY_CREDENTIALS_DOWNLOAD_URL}?${new URLSearchParams({ type: "docker" })}`
  }
]

export const getVMFiles = (props: {
  version: string
  packageSpecType: (typeof PACKAGE_SPEC_TYPE)[keyof typeof PACKAGE_SPEC_TYPE]
  OSPlatform?: OSPlatform
}): FileWithQueryString[] => {
  const { version, packageSpecType, OSPlatform } = props
  const fileName = "kortex"

  return [
    {
      OSPlatform: "linux_arm64" as const,
      name: `${fileName}-${version}-linux-arm64.tgz`,
      query: {
        packageSpecType,
        packageSpecArch: PACKAGE_SPEC_ARCH.ARM64
      } as const
    },
    {
      OSPlatform: "linux_amd64" as const,
      name: `${fileName}-${version}-linux-amd64.tgz`,
      query: {
        packageSpecType,
        packageSpecArch: PACKAGE_SPEC_ARCH.AMD64
      } as const
    },
    {
      OSPlatform: "windows_amd64" as const,
      name: `${fileName}-${version}-windows-amd64.exe`,
      query: {
        packageSpecType: PACKAGE_SPEC_TYPE.WINDOWS,
        packageSpecArch: PACKAGE_SPEC_ARCH.AMD64
      } as const
    }
  ].filter((item) => item.OSPlatform === OSPlatform || !OSPlatform)
}

// The Lambda layer zip of a version and architecture, as downloaded
export const getLambdaZipName = (
  version: string,
  packageSpecArch: (typeof PACKAGE_SPEC_ARCH)[keyof typeof PACKAGE_SPEC_ARCH]
) => `kortex-${version}-lambda-${packageSpecArch}.zip`

// Lambda layer zip (the extension and both sensors) of the selected architecture
export const getLambdaFiles = (props: {
  version: string
  packageSpecArch: (typeof PACKAGE_SPEC_ARCH)[keyof typeof PACKAGE_SPEC_ARCH]
}): FileWithQueryString[] => {
  const { version, packageSpecArch } = props

  return [
    {
      name: getLambdaZipName(version, packageSpecArch),
      query: { packageSpecType: PACKAGE_SPEC_TYPE.LAMBDA, packageSpecArch }
    }
  ]
}

// The Python code-upload package (the launcher, both sensors and the
// sitecustomize.py that starts them) of a version and architecture
export const getPythonZipName = (
  version: string,
  packageSpecArch: (typeof PACKAGE_SPEC_ARCH)[keyof typeof PACKAGE_SPEC_ARCH]
) => `kortex-${version}-python-${packageSpecArch}.zip`

export const getPythonFiles = (props: {
  version: string
  packageSpecArch: (typeof PACKAGE_SPEC_ARCH)[keyof typeof PACKAGE_SPEC_ARCH]
}): FileWithQueryString[] => {
  const { version, packageSpecArch } = props

  return [
    {
      name: getPythonZipName(version, packageSpecArch),
      query: { packageSpecType: PACKAGE_SPEC_TYPE.PYTHON, packageSpecArch }
    }
  ]
}
